import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api';
import { useAuthStore } from '../store/authStore';
import EyeIcon from '../components/common/EyeIcon';

const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', code: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSendCode = async () => {
    if (!form.email) { setError(t('auth.emailRequired')); return; }
    setError(''); setInfo(''); setSendingCode(true);
    try {
      await authApi.sendEmailCode(form.email);
      setCodeSent(true);
      setInfo(t('auth.codeSent'));
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.codeSendFailed'));
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.register(form);
      const { accessToken, refreshToken, userId, ...rest } = res.data.data;
      setAuth({ ...rest, id: userId } as any, accessToken);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src="/symbol.png" alt="Moa" style={styles.symbol} />
        <h1 style={styles.title}>{t('auth.registerTitle')}</h1>
        <p style={styles.sub}>{t('auth.registerSub')}</p>

        {error && <div style={styles.error}>{error}</div>}
        {info && <div style={styles.info}>{info}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>{t('auth.email')}</label>
            <div style={styles.row}>
              <input
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                style={{ ...styles.input, flex: 1 }}
                required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
              <button type="button" style={styles.sendBtn} onClick={handleSendCode} disabled={sendingCode}>
                {sendingCode ? t('auth.sendingCode') : codeSent ? t('auth.resendCode') : t('auth.sendCode')}
              </button>
            </div>
          </div>

          {codeSent && (
            <div style={styles.field}>
              <label style={styles.label}>{t('auth.verifyCode')}</label>
              <input
                type="text"
                inputMode="numeric"
                placeholder={t('auth.verifyCodePlaceholder')}
                style={styles.input}
                required
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value })}
              />
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>{t('auth.password')}</label>
            <div style={styles.passwordWrap}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('auth.passwordHint')}
                style={{ ...styles.input, paddingRight: 44, width: '100%', boxSizing: 'border-box' }}
                required
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
              />
              <button
                type="button"
                style={styles.eyeBtn}
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              ><EyeIcon open={showPassword} /></button>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('auth.name')}</label>
            <input
              type="text"
              placeholder={t('auth.namePlaceholder')}
              style={styles.input}
              required
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>{t('auth.phone')}</label>
            <input
              type="tel"
              placeholder={t('auth.phonePlaceholder')}
              style={styles.input}
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
            />
          </div>

          <button type="submit" style={styles.btn} disabled={loading || !codeSent}>
            {loading ? t('auth.processing') : t('auth.registerBtn')}
          </button>
        </form>

        <p style={styles.footer}>
          {t('auth.hasAccount')} <Link to="/login" style={styles.link}>{t('auth.goLogin')}</Link>
        </p>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' },
  card: { background: '#fff', padding: '48px 40px', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  symbol: { width: 72, height: 72, display: 'block', margin: '0 auto 12px' },
  title: { fontSize: 24, fontWeight: 700, color: '#333', textAlign: 'center', marginBottom: 8 },
  sub: { fontSize: 13, color: '#999', textAlign: 'center', marginBottom: 32 },
  error: { background: '#fff0f0', border: '1px solid #ffcdd2', borderRadius: 8, padding: '12px 16px', color: '#e53935', fontSize: 13, marginBottom: 20 },
  info: { background: '#f0f7ff', border: '1px solid #cde3ff', borderRadius: 8, padding: '12px 16px', color: '#2d7ad4', fontSize: 13, marginBottom: 20 },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  row: { display: 'flex', gap: 8, alignItems: 'stretch' },
  sendBtn: { padding: '0 16px', background: '#fff', color: '#b39ddb', border: '1px solid #b39ddb', borderRadius: 8, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer' },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 500, color: '#555' },
  input: { padding: '12px 16px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none' },
  passwordWrap: { position: 'relative' },
  eyeBtn: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#888', display: 'flex', alignItems: 'center' },
  btn: { padding: '14px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, marginTop: 8, cursor: 'pointer' },
  footer: { textAlign: 'center', fontSize: 13, color: '#888', marginTop: 24 },
  link: { color: '#b39ddb', fontWeight: 600 },
};

export default RegisterPage;
